// Snow layers (MB.1) — the state machine for the existing `snow` layer block:
// stacking on place, snowball loot, snowfall accumulation, and melt. PURE (ENG-1).
//
// The `snow` block already carries a `layers` 1..8 property and renders/collides
// at n/8 height (shapes.ts SHAPE.LAYER); this module owns the *behaviour* the host
// wires into placement, the break-drop path, and the random-tick handler.

/** A snow block piles no higher than eight layers (a full-height block). */
export const SNOW_MAX_LAYERS = 8;

/** Block-light strictly above this melts snow (Minecraft's threshold). Sky light
 *  never melts snow — only emitters (torch/lava/glowstone), so daytime snow lasts. */
export const SNOW_MELT_LIGHT = 11;

/** How high natural snowfall itself piles. Vanilla tops fresh accumulation at a
 *  single layer; TerraForge lets cold storms build depth (tunable here). */
export const SNOW_NATURAL_MAX = 8;

/** Layer count from a resolved block-state property map, clamped to 1..8. */
export function layersOf(props: Readonly<Record<string, string>> | undefined): number {
  return Math.max(1, Math.min(SNOW_MAX_LAYERS, Math.floor(Number(props?.layers ?? '1')) || 1));
}

/** Using a snow item on an n-layer snow block adds one layer (up to the cap);
 *  null once it is already at maximum height. */
export function stackPlace(current: number): number | null {
  const n = Math.max(1, Math.min(SNOW_MAX_LAYERS, Math.floor(current)));
  return n >= SNOW_MAX_LAYERS ? null : n + 1;
}

/** Snowballs a broken snow layer yields — one per layer, and only to a shovel
 *  (broken by hand or a non-shovel tool, a snow layer drops nothing, as in MC). */
export function snowballsFor(layers: number, hasShovel: boolean): number {
  if (!hasShovel) return 0;
  return Math.max(0, Math.min(SNOW_MAX_LAYERS, Math.floor(layers)));
}

/** The conditions under which snow forms/grows: cold biome, open sky, snowing. */
export interface SnowfallCtx {
  /** Overworld snow weather is falling on this column. */
  snowing: boolean;
  /** The cell has a clear vertical view of the sky (sky light 15). */
  seesSky: boolean;
  /** The biome here is cold enough for precipitation to fall as snow. */
  cold: boolean;
  /** How high natural fall may pile (defaults to SNOW_NATURAL_MAX). */
  maxNatural?: number;
}

/** New layer count for an existing snow block accumulating under snowfall, or
 *  null when nothing changes (not snowing / roofed / warm / already at the cap). */
export function accumulateStep(current: number, ctx: SnowfallCtx): number | null {
  if (!ctx.snowing || !ctx.seesSky || !ctx.cold) return null;
  const cap = ctx.maxNatural ?? SNOW_NATURAL_MAX;
  const n = layersOf({ layers: String(current) });
  return n < cap ? n + 1 : null;
}

/** Whether a fresh one-layer coat should form on a bare solid surface. */
export function shouldFormOnSurface(ctx: SnowfallCtx): boolean {
  return ctx.snowing && ctx.seesSky && ctx.cold;
}

/** Snow melts once the *block* light at it exceeds the threshold. */
export function shouldMelt(blockLight: number): boolean {
  return blockLight > SNOW_MELT_LIGHT;
}
